import React, { useEffect, useMemo, useState, useRef, Fragment } from 'react';
import { TrendingUp, TrendingDown, Leaf, AlertTriangle, Sparkles, Search, Activity, ArrowUpRight, FileText, Download, Code2, GitBranch, RefreshCw, Layers, Zap, CheckCircle2, Play, Clock, AlertCircle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { MiniSparkline } from '../components/charts/MiniSparkline';
import { useScreenInit } from '../useScreenInit.js';
// --- MOCK DATA ---
const generateDailyEmissions = () => {
  const data = [];
  const today = new Date();
  for (let i = 30; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    data.push({
      date: date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'short'
      }),
      OpenAI: Number((3 + Math.random() * 1.5).toFixed(2)),
      Anthropic: Number((1.5 + Math.random() * 1).toFixed(2)),
      Google: Number((0.3 + Math.random() * 0.5).toFixed(2))
    });
  }
  return data;
};
const DAILY_EMISSIONS = generateDailyEmissions();
const SERVICES_IMPACT = [{
  id: 's1',
  name: 'Customer Assistant',
  provider: 'OpenAI',
  model: 'GPT-4o',
  impact: 42,
  percent: 33.9,
  trend: [40, 42, 41, 43, 45, 44, 42],
  status: 'warning'
}, {
  id: 's2',
  name: 'Resume Screener',
  provider: 'OpenAI',
  model: 'GPT-4',
  impact: 31,
  percent: 25.0,
  trend: [20, 22, 25, 28, 30, 32, 31],
  status: 'danger'
}, {
  id: 's3',
  name: 'Smart Matcher',
  provider: 'Anthropic',
  model: 'Claude 3.5 Sonnet',
  impact: 22,
  percent: 17.7,
  trend: [25, 24, 23, 22, 21, 22, 22],
  status: 'success'
}, {
  id: 's4',
  name: 'Document Analyzer',
  provider: 'Google',
  model: 'Gemini Pro',
  impact: 16,
  percent: 12.9,
  trend: [15, 16, 15, 16, 17, 16, 16],
  status: 'success'
}, {
  id: 's5',
  name: 'Content Moderator',
  provider: 'OpenAI',
  model: 'GPT-4o-mini',
  impact: 8,
  percent: 6.5,
  trend: [10, 9, 8, 9, 8, 7, 8],
  status: 'success'
}];
const EFFICIENCY_DATA = [{
  model: 'GPT-4o-mini',
  provider: 'OpenAI',
  consumption: '0,15 Wh',
  co2: '0,07 kg',
  efficiency: 'Ottima',
  status: 'success'
}, {
  model: 'GPT-4o',
  provider: 'OpenAI',
  consumption: '0,30 Wh',
  co2: '0,14 kg',
  efficiency: 'Buona',
  status: 'success'
}, {
  model: 'Claude 3.5 Sonnet',
  provider: 'Anthropic',
  consumption: '0,35 Wh',
  co2: '0,16 kg',
  efficiency: 'Media',
  status: 'warning'
}, {
  model: 'Gemini Pro',
  provider: 'Google',
  consumption: '0,40 Wh',
  co2: '0,18 kg',
  efficiency: 'Media',
  status: 'warning'
}, {
  model: 'GPT-4',
  provider: 'OpenAI',
  consumption: '1,20 Wh',
  co2: '0,55 kg',
  efficiency: 'Bassa',
  status: 'danger'
}];
const PROVIDER_IMPACT = [{
  name: 'OpenAI',
  color: '#00B4D8',
  co2: 81,
  percent: 65.3,
  models: 3,
  topModel: 'GPT-4o',
  trend: '-12%'
}, {
  name: 'Anthropic',
  color: '#2A3B5C',
  co2: 22,
  percent: 17.7,
  models: 1,
  topModel: 'Claude 3.5 Sonnet',
  trend: '-8%'
}, {
  name: 'Google',
  color: '#10b981',
  co2: 16,
  percent: 12.9,
  models: 1,
  topModel: 'Gemini Pro',
  trend: '-3%'
}];
const SCAN_HISTORY = [{
  id: 'sc1',
  service: 'Customer Assistant',
  repo: 'cust-assist-ai',
  date: '26 Mar 2026',
  high: 1,
  medium: 2,
  low: 2,
  co2Savings: '~18 kg',
  status: 'Da risolvere'
}, {
  id: 'sc2',
  service: 'Resume Screener',
  repo: 'resume-screen-v3',
  date: '24 Mar 2026',
  high: 2,
  medium: 1,
  low: 1,
  co2Savings: '~12 kg',
  status: 'Da risolvere'
}, {
  id: 'sc3',
  service: 'Smart Matcher',
  repo: 'smart-matcher-core',
  date: '20 Mar 2026',
  high: 0,
  medium: 1,
  low: 2,
  co2Savings: '~4 kg',
  status: 'Completata'
}, {
  id: 'sc4',
  service: 'Document Analyzer',
  repo: 'doc-analyzer',
  date: '18 Mar 2026',
  high: 0,
  medium: 2,
  low: 3,
  co2Savings: '~7 kg',
  status: 'Da risolvere'
}, {
  id: 'sc5',
  service: 'Content Moderator',
  repo: 'content-mod-v2',
  date: '15 Mar 2026',
  high: 0,
  medium: 0,
  low: 1,
  co2Savings: '~2 kg',
  status: 'Completata'
}];
const SCAN_RESULTS = {
  service: 'Customer Assistant',
  date: new Date().toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }),
  duration: '1m 48s',
  filesScanned: 89,
  high: 1,
  medium: 2,
  low: 2,
  totalSavings: '~33 kg CO₂/mese',
  findings: [{
    severity: 'Alta',
    file: 'src/handlers/chat.ts',
    line: 87,
    desc: 'Il servizio effettua in media 3,2 chiamate API per sessione dove una sola basterebbe. Il modulo di intent detection viene invocato ripetutamente senza caching dei risultati intermedi.',
    rule: 'Best Practice: API Call Optimization',
    savings: '~14 kg CO₂/mese'
  }, {
    severity: 'Media',
    file: 'src/prompts/systemPrompt.ts',
    line: 23,
    desc: 'I prompt contengono istruzioni ripetute e contesto statico (~1.800 token) che potrebbe essere spostato in un template condiviso, riducendo i token per query del 40%.',
    rule: 'Best Practice: Prompt Engineering',
    savings: '~8 kg CO₂/mese'
  }, {
    severity: 'Media',
    file: 'src/services/contextBuilder.ts',
    line: 156,
    desc: 'Il contesto della conversazione include lo storico completo senza limite. Implementare una finestra di contesto ridurrebbe significativamente i token inviati.',
    rule: 'Best Practice: Context Window Management',
    savings: '~5 kg CO₂/mese'
  }, {
    severity: 'Bassa',
    file: 'src/config/models.ts',
    line: 12,
    desc: "Il fallback su GPT-4 scatta nel 12% dei casi per timeout. Aumentando il timeout da 5s a 8s si ridurrebbero i fallback al 2%, evitando l'uso del modello più pesante.",
    rule: 'Best Practice: Model Fallback Strategy',
    savings: '~4 kg CO₂/mese'
  }, {
    severity: 'Bassa',
    file: 'src/middleware/cache.ts',
    line: 45,
    desc: 'Manca caching delle risposte per query identiche. Il 15% delle richieste sono duplicati entro 1h che potrebbero essere serviti da cache.',
    rule: 'Best Practice: Response Caching',
    savings: '~2 kg CO₂/mese'
  }]
};
export function GreenOpsPage() {
  const screenInit = useScreenInit();
  const [activeTab, setActiveTab] = useState(screenInit.activeTab ?? 'Panoramica');
  const [period, setPeriod] = useState('30G');
  const [searchQuery, setSearchQuery] = useState('');
  // Scan state
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanComplete, setScanComplete] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState('Customer Assistant (repo: cust-assist-ai)');
  const scanIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startScan = () => {
    setIsScanning(true);
    setScanProgress(0);
    setScanComplete(false);
    scanIntervalRef.current = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 100) {
          if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
          setIsScanning(false);
          setScanComplete(true);
          return 100;
        }
        const increment = prev < 20 ? 2 : prev < 80 ? 4 : 2;
        return Math.min(prev + increment, 100);
      });
    }, 150);
  };
  useEffect(() => {
    return () => {
      if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
    };
  }, []);
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Completata':
        return <span className="bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full px-2.5 py-0.5 text-[11px] font-medium flex items-center gap-1 w-fit" data-id="element-3147" data-mp="18drhvlne1nw5">
            <CheckCircle2 className="w-3 h-3" data-id="element-3148" data-mp="1hepnu9he05hd" /> Completata
          </span>;
      case 'Da risolvere':
        return <span className="bg-amber-50 text-amber-600 border border-amber-100 rounded-full px-2.5 py-0.5 text-[11px] font-medium flex items-center gap-1 w-fit" data-id="element-3149" data-mp="1mekcs7hlqkzs">
            <AlertTriangle className="w-3 h-3" data-id="element-3150" data-mp="3j8xbmlmpzq4q" /> Da risolvere
          </span>;
      default:
        return null;
    }
  };
  const filteredServices = useMemo(() => {
    return SERVICES_IMPACT.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()) || s.provider.toLowerCase().includes(searchQuery.toLowerCase()) || s.model.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [searchQuery]);
  return <div className="min-h-screen bg-[#F4F6F8] p-4 md:p-8" data-id="element-3151" data-mp="1yojv1nxqt4oa">
      {/* 1. HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8" data-id="element-3152" data-mp="1yqw7jlkqemty">
        <div data-id="element-3153" data-mp="15zw173yxh6z4">
          <h1 className="text-[28px] md:text-[32px] font-semibold text-[#1A202C] tracking-tight" data-id="element-3154" data-mp="1604fn0ap8kcf">
            GreenOps
          </h1>
          <p className="text-[14px] text-gray-600 mt-1" data-id="element-3155" data-mp="1kxwt7mwoqynz">
            Dashboard di monitoraggio dell'impatto ambientale dei servizi AI.
          </p>
        </div>
        <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 shadow-sm" data-id="element-3156" data-mp="2wc20opw3xqo2">
          {['7G', '30G', '90G', '12M'].map(p => <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors ${period === p ? 'bg-[#F4F6F8] text-[#2A3B5C] shadow-sm' : 'text-gray-500 hover:text-[#1A202C]'}`} data-id="element-3157" data-mp="2rcfn2fcda8eq">
              {p}
            </button>)}
        </div>
      </div>

      {/* 2. KPI CARDS ROW */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-3158" data-mp="2py4usyj3m8sn">
        {/* Card 1 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-3159" data-mp="2tzgl769sqy1w">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-3160" data-mp="3hpkcce5st5c0">
            Impatto CO₂ Totale Mese
          </h3>
          <div className="flex items-baseline gap-2 mt-auto" data-id="element-3161" data-mp="38v5ymg0x22v0">
            <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-3162" data-mp="38unwj2rkouf6">
              124 kg CO₂
            </span>
            <span className="text-[12px] text-emerald-600 font-medium flex items-center gap-0.5" data-id="element-3163" data-mp="3cu517tz6dnja">
              <TrendingDown className="w-3.5 h-3.5" data-id="element-3164" data-mp="4ev3ey2bj4hb" /> -15%
            </span>
          </div>
          <p className="text-[11px] text-gray-400 mt-1" data-id="element-3165" data-mp="99ith1cz06y0">vs mese precedente</p>
        </div>

        {/* Card 2 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-3166" data-mp="1sl4w3pnsumps">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-3167" data-mp="1xjpxvtip9egc">
            Impatto Medio per Query
          </h3>
          <div className="flex items-baseline gap-2 mt-auto" data-id="element-3168" data-mp="1nnd8v8y3norx">
            <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-3169" data-mp="1npze31xips6z">
              0,42 Wh
            </span>
          </div>
          <p className="text-[11px] text-gray-400 mt-1" data-id="element-3170" data-mp="1en83f4cgv0ge">
            vs 0,48 Wh mese prec.
          </p>
        </div>

        {/* Card 3 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-3171" data-mp="zzmxz3s4cp5b">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-3172" data-mp="14y6mnby3kf1d">
            Servizio più Green
          </h3>
          <div className="flex items-baseline gap-2 mt-auto flex-wrap" data-id="element-3173" data-mp="qy5innmjcrho">
            <span className="text-[20px] font-bold text-[#1A202C] leading-tight" data-id="element-3174" data-mp="uy5y9bldf8qb">
              Content Moderator
            </span>
            <span className="bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full px-2 py-0.5 text-[10px] font-medium" data-id="element-3175" data-mp="cb6vw5ymil9a">
              Più efficiente
            </span>
          </div>
          <p className="text-[11px] text-gray-400 mt-1" data-id="element-3176" data-mp="lcwohf48rq8n">GPT-4o-mini</p>
        </div>

        {/* Card 4 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-3177" data-mp="gems516s6jlg">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-3178" data-mp="v7oik8ym7uyo">
            Potenziale Risparmio
          </h3>
          <div className="mt-auto" data-id="element-3179" data-mp="14238d5grunw1">
            <div className="flex items-baseline gap-1 mb-2" data-id="element-3180" data-mp="1411ku1ikk8xf">
              <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-3181" data-mp="100ho2z1wr7bt">
                -18 kg CO₂/mese
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5 mb-1" data-id="element-3182" data-mp="1e1bzlqk95oep">
              <div className="bg-amber-500 h-1.5 rounded-full" style={{
              width: '45%'
            }} data-id="element-3183" data-mp="191pnjkg5ue7h"></div>
            </div>
            <p className="text-[10px] text-gray-400 leading-tight" data-id="element-3184" data-mp="6p2xjijtwv96">
              Applicando le ottimizzazioni suggerite
            </p>
          </div>
        </div>
      </div>

      {/* 3. TABS NAVIGATION */}
      <div className="border-b-2 border-gray-300 mb-6" data-id="element-3185" data-mp="fn52uaalia29">
        <div className="-mb-[2px] flex items-center gap-8 overflow-x-auto custom-scrollbar" data-id="element-3186" data-mp="kfpqsv6tlsxv">
          {['Panoramica', 'Per Servizio', 'Per Provider', 'Code Scan'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-[15px] font-medium transition-colors whitespace-nowrap border-b-2 ${activeTab === tab ? 'border-[#00B4D8] text-[#2A3B5C]' : 'border-transparent text-gray-500 hover:text-gray-800'}`} data-id="element-3187" data-mp="pglup8r69f3a">
                {tab}
              </button>)}
        </div>
      </div>

      {/* 4. TAB CONTENT */}
      {activeTab === 'Panoramica' && <div className="space-y-6" data-id="element-3188" data-mp="308otux2r55ay">
          {/* ROW 3: Chart & Ranking */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3189" data-mp="355njp3fduq3c">
            {/* Area Chart */}
            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-id="element-3190" data-mp="2xn799ozlgclv">
              <div className="flex justify-between items-center mb-6" data-id="element-3191" data-mp="2stuz2jvnw8cn">
                <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-3192" data-mp="2nx4fvnok8wn0">
                  Trend Impatto CO₂ Mensile
                </h2>
                <div className="flex items-center gap-4 text-[11px] font-medium" data-id="element-3193" data-mp="2iux4az7n7il7">
                  <div className="flex items-center gap-1.5" data-id="element-3194" data-mp="3gjz14mt7c01j">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#00B4D8]" data-id="element-3195" data-mp="3ble0ima1feck"></div>
                    OpenAI
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-3196" data-mp="36kzsv5ccm6f2">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#2A3B5C]" data-id="element-3197" data-mp="32lqxjw1nu0he"></div>
                    Anthropic
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-3198" data-mp="1v8yuqztlh5mn">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#10b981]" data-id="element-3199" data-mp="1r5kgadlb0xbb"></div>
                    Google
                  </div>
                </div>
              </div>
              <div className="h-[360px] w-full" data-id="element-3200" data-mp="3jt8f339ay4ud">
                <ResponsiveContainer width="100%" height="100%" data-id="element-3201" data-mp="3ju0949n93uij">
                  <AreaChart data={DAILY_EMISSIONS} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-3202" data-mp="354ucmtfssiec">
                    <defs data-id="element-3203" data-mp="1nlv6aozfkk5">
                      <linearGradient id="greenColorOpenAI" x1="0" y1="0" x2="0" y2="1" data-id="element-3204" data-mp="1nc38hkdn3bt">
                        <stop offset="5%" stopColor="#00B4D8" stopOpacity={0.3} data-id="element-3205" data-mp="6n87mhwzkwrw" />
                        <stop offset="95%" stopColor="#00B4D8" stopOpacity={0} data-id="element-3206" data-mp="3oslawdi5im93" />
                      </linearGradient>
                      <linearGradient id="greenColorAnthropic" x1="0" y1="0" x2="0" y2="1" data-id="element-3207" data-mp="3st58tjajttwa">
                        <stop offset="5%" stopColor="#2A3B5C" stopOpacity={0.3} data-id="element-3208" data-mp="2dcmvzt0al5zy" />
                        <stop offset="95%" stopColor="#2A3B5C" stopOpacity={0} data-id="element-3209" data-mp="2herkkdbvvcfq" />
                      </linearGradient>
                      <linearGradient id="greenColorGoogle" x1="0" y1="0" x2="0" y2="1" data-id="element-3210" data-mp="29upcm6eknujm">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} data-id="element-3211" data-mp="24wnlcy6zsox2" />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} data-id="element-3212" data-mp="2jtembbwbe8os" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-3213" data-mp="2evd2gor2e5c9" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#6B7280'
                }} dy={10} minTickGap={30} data-id="element-3214" data-mp="1r5slgbeh1i5m" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#6B7280'
                }} tickFormatter={val => `${val} kg`} domain={[0, 6]} data-id="element-3215" data-mp="20usp483x51q5" />
                    <Tooltip contentStyle={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                  fontSize: '12px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }} itemStyle={{
                  color: '#1A202C',
                  fontWeight: 500
                }} formatter={(value: number) => [`${value} kg CO₂`, undefined]} data-id="element-3216" data-mp="18d7wv65t1z7m" />
                    <Area type="monotone" dataKey="Google" stackId="1" stroke="#10b981" fill="url(#greenColorGoogle)" strokeWidth={2} data-id="element-3217" data-mp="26up70g1qvjx1" />
                    <Area type="monotone" dataKey="Anthropic" stackId="1" stroke="#2A3B5C" fill="url(#greenColorAnthropic)" strokeWidth={2} data-id="element-3218" data-mp="1a5hlv951fctn" />
                    <Area type="monotone" dataKey="OpenAI" stackId="1" stroke="#00B4D8" fill="url(#greenColorOpenAI)" strokeWidth={2} data-id="element-3219" data-mp="1e84a702i2fm3" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Top Services */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 flex flex-col" data-id="element-3220" data-mp="2ujrpfq47dgsl">
              <div className="flex justify-between items-center mb-4" data-id="element-3221" data-mp="2zht8wc5jji4l">
                <h2 className="text-[14px] font-semibold text-[#1A202C]" data-id="element-3222" data-mp="nwakspze86an">
                  Top Servizi per Impatto
                </h2>
                <button className="text-[11px] text-[#00B4D8] font-medium hover:underline" data-id="element-3223" data-mp="ixpsrduaosxa">
                  Vedi tutti
                </button>
              </div>
              <div className="flex-1 flex flex-col gap-0 divide-y divide-gray-100" data-id="element-3224" data-mp="963bazrd5xm3">
                {SERVICES_IMPACT.map((service, idx) => <div key={service.id} className="flex items-center justify-between py-2.5" data-id="element-3225" data-mp="5748plt3ucxt" data-mpi={idx}>
                    <div className="flex items-center gap-2.5 overflow-hidden" data-id="element-3226" data-mp="4vdsjwo90t2">
                      <span className="text-[11px] font-semibold text-gray-400 w-4 text-center shrink-0" data-id="element-3227" data-mp="3rdsp4nfyiv6b">
                        {idx + 1}
                      </span>
                      <p className="text-[13px] font-medium text-[#1A202C] truncate" data-id="element-3228" data-mp="1pl4ie443tpls">
                        {service.name}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 pl-2" data-id="element-3229" data-mp="1kkguhrb69ovs">
                      <span className="text-[12px] font-semibold text-[#1A202C]" data-id="element-3230" data-mp="thh5dxg1yqa7">
                        {service.impact} kg
                      </span>
                      <span className="text-[10px] text-gray-400 w-10 text-right" data-id="element-3231" data-mp="kp5kss7mdx25">
                        {service.percent}%
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* ROW 4: Efficiency Table */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-3232" data-mp="fq2cmywcfapy">
            <div className="px-6 py-5 border-b border-gray-100" data-id="element-3233" data-mp="1q9onbc7b8lo">
              <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-3234" data-mp="1ruhmwivajhf">
                Comparazione Efficienza Modelli
              </h2>
              <p className="text-[13px] text-gray-500 mt-1" data-id="element-3235" data-mp="1w398jgnm6oyr">
                Stesso task, impatto diverso
              </p>
            </div>
            <div className="overflow-x-auto" data-id="element-3236" data-mp="204l4s50zu8fx">
              <table className="w-full text-left border-collapse" data-id="element-3237" data-mp="3kn5blek29tk5">
                <thead data-id="element-3238" data-mp="3klc2lslz6x8z">
                  <tr className="bg-gray-50/50 border-b border-gray-100" data-id="element-3239" data-mp="3gm3eb39pfrkt">
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3240" data-mp="3gk0cllz6wa09">
                      Modello
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3241" data-mp="3plq6cygjnl17">
                      Provider
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-3242" data-mp="37ghypcm6d0f">
                      Consumo (Wh/query)
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-3243" data-mp="c7v5zwgyq67m">
                      CO₂/1000 query
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-3244" data-mp="2dyzuwkrigso9">
                      Efficienza
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-3245" data-mp="1hc7t8rl0yon8">
                  {EFFICIENCY_DATA.map((row, idx) => <tr key={idx} className="hover:bg-gray-50/50 transition-colors" data-id="element-3246" data-mp="1v3co7gdg6sr8" data-mpi={idx}>
                      <td className="px-6 py-4 text-[13px] font-medium text-[#2a3a54]" data-id="element-3247" data-mp="1r1ho2f796lf1">
                        {row.model}
                      </td>
                      <td className="px-6 py-4 text-[13px] text-gray-600" data-id="element-3248" data-mp="13c6q15haf54l">
                        {row.provider}
                      </td>
                      <td className="px-6 py-4 text-[13px] text-gray-600 text-right" data-id="element-3249" data-mp="1cc3b6x4gxluw">
                        {row.consumption}
                      </td>
                      <td className="px-6 py-4 text-[13px] text-gray-600 text-right" data-id="element-3250" data-mp="17bp3jg6s4dxe">
                        {row.co2}
                      </td>
                      <td className="px-6 py-4 text-center" data-id="element-3251" data-mp="2isnoyxussn5d">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${row.status === 'success' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : row.status === 'warning' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-red-50 text-red-600 border-red-100'}`} data-id="element-3252" data-mp="2is5n9lpd9gq2">
                          {row.status === 'success' && '🟢 '}
                          {row.status === 'warning' && '🟡 '}
                          {row.status === 'danger' && '🔴 '}
                          {row.efficiency}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-3 bg-gray-50/50 border-t border-gray-100" data-id="element-3253" data-mp="309uc2knjf08s">
              <p className="text-[11px] text-gray-400" data-id="element-3254" data-mp="357w3bsv4a5vc">
                Stime basate su benchmark pubblici. Consumo varia in base a data
                center e mix energetico regionale.
              </p>
            </div>
          </div>

          {/* ROW 6: Suggestions & Reports */}
          <h2 className="text-[16px] font-semibold text-[#1A202C] mt-8 mb-4" data-id="element-3255" data-mp="bf1cp4jzvgqw">
            Suggerimenti & Report
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3256" data-mp="6hstd0n416o2">
            {/* Card 1 */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-start gap-4 shadow-sm" data-id="element-3257" data-mp="1gbutbs3lwan">
              <div className="p-2 bg-emerald-50 rounded-lg shrink-0 mt-0.5" data-id="element-3258" data-mp="3tlfn4tj218kj">
                <Leaf className="w-5 h-5 text-emerald-600" data-id="element-3259" data-mp="3one7kiku7rdt" />
              </div>
              <div className="flex-1" data-id="element-3260" data-mp="3jvmi6bdf3rp9">
                <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-3261" data-mp="3jukzplu2o2xi">
                  <h3 className="text-[14px] font-semibold text-[#1A202C]" data-id="element-3262" data-mp="3exkybo863ocq">
                    Ottimizzazione Green
                  </h3>
                  <span className="bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide border border-emerald-100" data-id="element-3263" data-mp="39vvkk0tk8wky">
                    RISPARMIO STIMATO: -31 KG CO₂/MESE
                  </span>
                </div>
                <p className="text-[13px] text-gray-600 leading-relaxed mb-4" data-id="element-3264" data-mp="1gehgcypc8h24">
                  Il servizio <strong data-id="element-3265" data-mp="1lcrbxaez5jo9">Resume Screener</strong> utilizza GPT-4
                  (1,20 Wh/query). Passando a <strong data-id="element-3266" data-mp="1pgqo5gt81wbt">GPT-4o</strong> potresti
                  ridurre l'impatto ambientale del 75% mantenendo performance
                  comparabili.
                </p>
                <button className="text-[12px] font-medium text-[#00B4D8] hover:text-[#0096b4] flex items-center gap-1" data-id="element-3267" data-mp="xp1cc1eg26gy">
                  Applica modifica <ArrowUpRight className="w-3.5 h-3.5" data-id="element-3268" data-mp="11qmqrr6mmst9" />
                </button>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-start gap-4 shadow-sm" data-id="element-3269" data-mp="1bfmq5bp1jq7z">
              <div className="p-2 bg-blue-50 rounded-lg shrink-0 mt-0.5" data-id="element-3270" data-mp="e124ozf6fffr">
                <FileText className="w-5 h-5 text-[#00B4D8]" data-id="element-3271" data-mp="izlrf21lgv96" />
              </div>
              <div className="flex-1 flex flex-col h-full" data-id="element-3272" data-mp="ixiq3luzrfp5">
                <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-3273" data-mp="2lnbv55n5y98v">
                  <h3 className="text-[14px] font-semibold text-[#1A202C]" data-id="element-3274" data-mp="2gnpkn3ypz73r">
                    Report Sostenibilità
                  </h3>
                  <span className="bg-blue-50 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide border border-blue-100" data-id="element-3275" data-mp="2ug5epf9n7usj">
                    PRONTO PER IL BILANCIO
                  </span>
                </div>
                <p className="text-[13px] text-gray-600 leading-relaxed mb-4" data-id="element-3276" data-mp="2zdxjh9vdzbe5">
                  Genera il report di impatto ambientale dei tuoi servizi AI.
                  Include metriche per provider, trend mensili e comparazione
                  modelli. Disponibile in CSV e PDF.
                </p>
                <div className="mt-auto" data-id="element-3277" data-mp="1iz5t12hswe7h">
                  <button className="flex items-center justify-center px-4 py-2 bg-[#2A3B5C] text-white rounded-md hover:bg-[#1E2A40] transition-colors gap-2" data-id="element-3278" data-mp="1izxl43c6vtt0">
                    <span className="text-[13px] font-heading uppercase tracking-wide whitespace-nowrap" data-id="element-3279" data-mp="14tdjnsibtrq7">
                      Genera Report
                    </span>
                    <ArrowUpRight className="w-3.5 h-3.5" data-id="element-3280" data-mp="18xbgfwk6iqg3" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* TAB 2: PER SERVIZIO */}
      {activeTab === 'Per Servizio' && <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col" data-id="element-3281" data-mp="l5mucxxyay7l">
          <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4" data-id="element-3282" data-mp="q625diurftme">
            <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-3283" data-mp="c6st2u9odjnp">
              Dettaglio Impatto per Servizio
            </h2>
            <div className="relative w-full sm:w-72" data-id="element-3284" data-mp="h65m5wr53myq">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-3285" data-mp="26kk869arwvqj" />
              <input type="text" placeholder="Cerca servizio, provider o modello..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-[13px] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8] transition-all" data-id="element-3286" data-mp="2bh0yna5g3m6s" />
            </div>
          </div>
          <div className="overflow-x-auto" data-id="element-3287" data-mp="1v4fkhbq973kv">
            <table className="w-full text-left border-collapse" data-id="element-3288" data-mp="1v5h07ti7se8x">
              <thead data-id="element-3289" data-mp="2srxsulg063oc">
                <tr className="bg-gray-50/50 border-b border-gray-100" data-id="element-3290" data-mp="2sr4tw6nacigr">
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3291" data-mp="2nsbgee800rgl">
                    Servizio
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3292" data-mp="2izib9p8mbh3z">
                    Provider & Modello
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-3293" data-mp="3bjgtwmqgv7nq">
                    CO₂ (kg/mese)
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-3294" data-mp="3e3n6tjx268gf">
                    % Totale
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-3295" data-mp="3o08giik0w7ql">
                    Trend (7gg)
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-3296" data-mp="w204vnhgt9r">
                    Efficienza
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-3297" data-mp="am4yzchad1g0">
                {filteredServices.map(service => <tr key={service.id} className="hover:bg-gray-50/50 transition-colors group cursor-pointer" data-id="element-3298" data-mp="fkor5umh39gt">
                    <td className="px-6 py-4" data-id="element-3299" data-mp="kkksfm3scmso">
                      <div className="flex items-center gap-3" data-id="element-3300" data-mp="klvw7lx8dujs">
                        <div className={`w-2 h-2 rounded-full ${service.status === 'danger' ? 'bg-red-500' : service.status === 'warning' ? 'bg-amber-500' : 'bg-emerald-500'}`} data-id="element-3301" data-mp="omo7sltksbj8"></div>
                        <span className="text-[13px] font-medium text-[#2a3a54]" data-id="element-3302" data-mp="15vj3ux81aayd">
                          {service.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-3303" data-mp="1evei5awvym2m">
                      <div className="flex flex-col" data-id="element-3304" data-mp="1etl9xr6mjtsi">
                        <span className="text-[13px] text-[#1A202C] font-medium" data-id="element-3305" data-mp="r5bvjabqq8be">
                          {service.provider}
                        </span>
                        <span className="text-[11px] text-gray-500" data-id="element-3306" data-mp="m55y1dishrle">
                          {service.model}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-3307" data-mp="w33ng4cdxufl">
                      <span className="text-[13px] font-semibold text-[#1A202C]" data-id="element-3308" data-mp="8ekedmlihdmg">
                        {service.impact} kg
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-3309" data-mp="2f3cnypvi61x3">
                      <span className="text-[13px] text-gray-600" data-id="element-3310" data-mp="2f1rt0z5a0gyp">
                        {service.percent}%
                      </span>
                    </td>
                    <td className="px-6 py-4 w-32" data-id="element-3311" data-mp="264hd30hik3ng">
                      <MiniSparkline data={service.trend} color={service.status === 'danger' ? '#ef4444' : '#00B4D8'} className="h-[24px]" data-id="element-3312" data-mp="2a4rl0iov944y" />
                    </td>
                    <td className="px-6 py-4 text-center" data-id="element-3313" data-mp="2xv5co83jh0z0">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-medium border ${service.status === 'success' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : service.status === 'warning' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-red-50 text-red-600 border-red-100'}`} data-id="element-3314" data-mp="32tp2wkp60yx6">
                        {service.status === 'success' ? 'Efficiente' : service.status === 'warning' ? 'Nella media' : 'Inefficiente'}
                      </span>
                    </td>
                  </tr>)}
                {filteredServices.length === 0 && <tr data-id="element-3315" data-mp="lf6jxxo3cyih">
                    <td colSpan={6} className="px-6 py-8 text-center text-[13px] text-gray-500" data-id="element-3316" data-mp="lenc59s5nqi6">
                      Nessun servizio trovato per "{searchQuery}"
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>}

      {/* TAB 3: PER PROVIDER */}
      {activeTab === 'Per Provider' && <div className="space-y-6" data-id="element-3317" data-mp="3tt5ecuvf0btv">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-3318" data-mp="2op3argj9tdh">
            {PROVIDER_IMPACT.map(provider => <div key={provider.name} className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 flex flex-col" data-id="element-3319" data-mp="6q0mof8tdid8">
                <div className="flex items-center gap-2 mb-4" data-id="element-3320" data-mp="biaiud0iveny">
                  <div className="w-3 h-3 rounded-full" style={{
              backgroundColor: provider.color
            }} data-id="element-3321" data-mp="3b50fo1tm7h1x"></div>
                  <h3 className="text-[15px] font-semibold text-[#1A202C]" data-id="element-3322" data-mp="3f5akvc9l23fq">
                    {provider.name}
                  </h3>
                </div>
                <div className="mb-4" data-id="element-3323" data-mp="32nqdc5k59ioa">
                  <p className="text-[24px] font-bold text-[#1A202C]" data-id="element-3324" data-mp="32ot3gb9n7h35">
                    {provider.co2} kg CO₂
                  </p>
                  <p className="text-[12px] font-medium mt-1 text-emerald-500" data-id="element-3325" data-mp="2ykbmskykxjwl">
                    {provider.trend} vs mese prec.
                  </p>
                </div>
                <div className="mt-auto space-y-2 pt-4 border-t border-gray-100" data-id="element-3326" data-mp="2ooqph04gh7t6">
                  <div className="flex justify-between items-center" data-id="element-3327" data-mp="2jx07c7za1fr7">
                    <span className="text-[12px] text-gray-500" data-id="element-3328" data-mp="2ey5jusqd730r">
                      Quota impatto
                    </span>
                    <span className="text-[12px] font-medium text-[#1A202C]" data-id="element-3329" data-mp="2axd8ntxxmo1u">
                      {provider.percent}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-3330" data-mp="20yeax6epgctm">
                    <span className="text-[12px] text-gray-500" data-id="element-3331" data-mp="210h7yae9z67u">
                      Modelli attivi
                    </span>
                    <span className="text-[12px] font-medium text-[#1A202C]" data-id="element-3332" data-mp="1w9ljfni3geh4">
                      {provider.models}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-3333" data-mp="1hefez4yncnpw">
                    <span className="text-[12px] text-gray-500" data-id="element-3334" data-mp="1mcguxh0s06x5">
                      Modello top
                    </span>
                    <span className="text-[12px] font-medium text-[#1A202C]" data-id="element-3335" data-mp="2a21o0sqn8kk5">
                      {provider.topModel}
                    </span>
                  </div>
                </div>
              </div>)}
          </div>
        </div>}

      {/* TAB 4: CODE SCAN */}
      {activeTab === 'Code Scan' && <div className="space-y-6" data-id="element-3336" data-mp="258qe2iqjbno0">
          {/* Top Bar - Scan Controls */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-3337" data-mp="2szcg9tduydq3">
            <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-3338" data-mp="mbvjfffai7ip">
              <div className="p-2 bg-emerald-50 rounded-lg shrink-0" data-id="element-3339" data-mp="ma0uzr4t7x9v">
                <Code2 className="w-5 h-5 text-emerald-600" data-id="element-3340" data-mp="hbzf1f2oke2m" />
              </div>
              <div data-id="element-3341" data-mp="w1ya38yxv9dv">
                <h2 className="text-[15px] font-semibold text-[#1A202C]" data-id="element-3342" data-mp="w0dhhow6q0in">
                  Analisi Codice Sorgente
                </h2>
                <p className="text-[12px] text-gray-500 mt-0.5" data-id="element-3343" data-mp="r4y4ez6048na">
                  Scansiona i repository per individuare ottimizzazioni GreenOps
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-3344" data-mp="3ulndoswyxwgn">
              <select value={selectedRepo} onChange={e => setSelectedRepo(e.target.value)} className="flex-1 sm:w-64 bg-gray-50 border border-gray-200 text-[#1A202C] px-3 py-2.5 rounded-lg text-[13px] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8]" disabled={isScanning} data-id="element-3345" data-mp="cf8x0m7bf8ek">
                <option data-id="element-3346" data-mp="1jqzordyd7grl">Customer Assistant (repo: cust-assist-ai)</option>
                <option data-id="element-3347" data-mp="2f2hbtm59mqmp">Smart Matcher (repo: smart-matcher-core)</option>
                <option data-id="element-3348" data-mp="2j2hp33gmovs6">Content Moderator (repo: content-mod-v2)</option>
                <option data-id="element-3349" data-mp="2j4ug064d6ghd">AI Tutor (repo: ai-tutor-platform)</option>
                <option data-id="element-3350" data-mp="2o0j8xqlvoz4p">Resume Screener (repo: resume-screen-v3)</option>
                <option data-id="element-3351" data-mp="2t1gywpd4qxgr">Document Analyzer (repo: doc-analyzer)</option>
              </select>
              <button onClick={startScan} disabled={isScanning} className="flex items-center justify-center gap-2 px-6 py-2.5 bg-[#2A3B5C] text-white rounded-lg hover:bg-[#1E2A40] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed shrink-0" data-id="element-3352" data-mp="2y13dp38lwlro">
                <Play className="w-4 h-4" data-id="element-3353" data-mp="3brozazpg39t9" />
                <span className="text-[14px] font-medium" data-id="element-3354" data-mp="3bqnc5wv5mwv6">
                  {isScanning ? 'Scansione in corso...' : 'Avvia Scansione'}
                </span>
              </button>
            </div>
          </div>

          {/* Scan in Progress */}
          {isScanning && <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 shadow-sm" data-id="element-3355" data-mp="1a29c6g2exqko">
              <div className="flex justify-between items-center mb-4" data-id="element-3356" data-mp="153fyamj7rxjz">
                <div className="flex items-center gap-3" data-id="element-3357" data-mp="155ix9w2ch10u">
                  <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-3358" data-mp="105eeu0hvlw7z">
                    <Search className="w-5 h-5 text-emerald-600 animate-pulse" data-id="element-3359" data-mp="v59zidspf7jc" />
                  </div>
                  <div data-id="element-3360" data-mp="1ssr3cg7lke3x">
                    <h3 className="text-[15px] font-semibold text-emerald-900" data-id="element-3361" data-mp="1stacb7f9rs5t">
                      Scansione in corso: {selectedRepo.split(' (')[0]}
                    </h3>
                    <p className="text-[12px] text-emerald-700 mt-0.5" data-id="element-3362" data-mp="1j2xs5gbda107">
                      {scanProgress < 30 ? 'Analisi struttura del progetto e dipendenze...' : scanProgress < 60 ? 'Scansione codice sorgente per pattern di chiamate API...' : scanProgress < 85 ? 'Verifica best practice GreenOps e caching...' : 'Generazione report e stima risparmio CO₂...'}
                    </p>
                  </div>
                </div>
                <span className="text-[14px] font-bold text-emerald-700" data-id="element-3363" data-mp="4waww1w32v4h">
                  {scanProgress}%
                </span>
              </div>
              <div className="w-full bg-emerald-200/50 rounded-full h-2" data-id="element-3364" data-mp="3rau7dnhxtq5o">
                <div className="bg-emerald-600 h-2 rounded-full transition-all duration-200 ease-out" style={{
            width: `${scanProgress}%`
          }} data-id="element-3365" data-mp="3raki624pvn11"></div>
              </div>
              <div className="flex items-center gap-4 mt-3 text-[11px] text-emerald-600" data-id="element-3366" data-mp="3i8kz74a568vg">
                <span data-id="element-3367" data-mp="3mciwrajtjbme">
                  File analizzati: {Math.floor(scanProgress * 0.89)}/89
                </span>
                <span data-id="element-3368" data-mp="38dsyhrl9j5wg">•</span>
                <span data-id="element-3369" data-mp="3db1mw9x08665">Tempo trascorso: {Math.floor(scanProgress * 0.15)}s</span>
              </div>
            </div>}

          {/* Scan Complete - Results */}
          {scanComplete && <div className="space-y-4" data-id="element-3370" data-mp="16iim5e62xuwc">
              {/* Summary Card */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-id="element-3371" data-mp="1bidb3c6aoaer">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6" data-id="element-3372" data-mp="2w2iiot29sapa">
                  <div className="flex items-center gap-3" data-id="element-3373" data-mp="2r3yusn448oua">
                    <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-3374" data-mp="35ra6qq47maux">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" data-id="element-3375" data-mp="35tmw3ocarni0" />
                    </div>
                    <div data-id="element-3376" data-mp="30yqtxvdcjjr6">
                      <h3 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-3377" data-mp="3eryjlhhva568">
                        Scansione completata: {selectedRepo.split(' (')[0]}
                      </h3>
                      <p className="text-[12px] text-gray-500 mt-0.5" data-id="element-3378" data-mp="39vhuol2ufms3">
                        {SCAN_RESULTS.date} • Durata: {SCAN_RESULTS.duration} •{' '}
                        {SCAN_RESULTS.filesScanned} file analizzati
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-3379" data-mp="1ue80anvtr1oh">
                    <span className="bg-emerald-50 text-emerald-700 text-[11px] font-bold px-3 py-1.5 rounded-lg uppercase tracking-wide border border-emerald-100" data-id="element-3380" data-mp="1pellia0clddk">
                      Risparmio: {SCAN_RESULTS.totalSavings}
                    </span>
                    <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-[13px] font-medium" data-id="element-3381" data-mp="1da69bt3fm5lk">
                      <Download className="w-4 h-4" data-id="element-3382" data-mp="1ib3odwt5ajiu" />
                      Esporta Report
                    </button>
                  </div>
                </div>

                {/* Severity Summary */}
                <div className="grid grid-cols-3 gap-4" data-id="element-3383" data-mp="pm6kwztlera5">
                  <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-center" data-id="element-3384" data-mp="uin7vqp22zln">
                    <p className="text-[28px] font-bold text-red-600" data-id="element-3385" data-mp="ym2zu9q6kbs0">
                      {SCAN_RESULTS.high}
                    </p>
                    <p className="text-[12px] font-medium text-red-700 mt-1" data-id="element-3386" data-mp="260o9al8uzqur">
                      Impatto Alto
                    </p>
                  </div>
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 text-center" data-id="element-3387" data-mp="3uh6ard4guq9r">
                    <p className="text-[28px] font-bold text-amber-600" data-id="element-3388" data-mp="3qhp4ihh7tp3j">
                      {SCAN_RESULTS.medium}
                    </p>
                    <p className="text-[12px] font-medium text-amber-700 mt-1" data-id="element-3389" data-mp="3lntbltft6sf8">
                      Impatto Medio
                    </p>
                  </div>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-center" data-id="element-3390" data-mp="3bsj7n05uoayt">
                    <p className="text-[28px] font-bold text-blue-600" data-id="element-3391" data-mp="36rvi6ix9s26p">
                      {SCAN_RESULTS.low}
                    </p>
                    <p className="text-[12px] font-medium text-blue-700 mt-1" data-id="element-3392" data-mp="32swfz62xajiy">
                      Impatto Basso
                    </p>
                  </div>
                </div>
              </div>

              {/* Findings Detail */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-3393" data-mp="w0c3oj2q5egr">
                <div className="px-6 py-5 border-b border-gray-100" data-id="element-3394" data-mp="a45auazy3pch">
                  <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-3395" data-mp="54semzn6p5x8">
                    Dettaglio Findings ({SCAN_RESULTS.findings.length})
                  </h2>
                </div>
                <div className="divide-y divide-gray-100" data-id="element-3396" data-mp="7ipll3piw9x">
                  {SCAN_RESULTS.findings.map((finding, idx) => {
              const iconBg = finding.severity === 'Alta' ? 'bg-red-50 border-red-100' : finding.severity === 'Media' ? 'bg-amber-50 border-amber-100' : 'bg-blue-50 border-blue-100';
              const badgeClass = finding.severity === 'Alta' ? 'bg-red-50 text-red-600 border-red-100' : finding.severity === 'Media' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-blue-50 text-blue-600 border-blue-100';
              const iconColor = finding.severity === 'Alta' ? 'text-red-500' : finding.severity === 'Media' ? 'text-amber-500' : 'text-blue-500';
              return <div key={idx} className="px-6 py-4 hover:bg-gray-50/50 transition-colors" data-id="element-3397" data-mp="16s1ksx0vnmeq" data-mpi={idx}>
                        <div className="flex items-start gap-4" data-id="element-3398" data-mp="2piz1l4y65rs4">
                          <div className={`mt-0.5 p-1.5 rounded-md border shrink-0 ${iconBg}`} data-id="element-3399" data-mp="2ugs51q8387op">
                            {finding.severity === 'Alta' ? <AlertTriangle className={`w-4 h-4 ${iconColor}`} data-id="element-3400" data-mp="2yjospvchitzi" /> : finding.severity === 'Media' ? <AlertCircle className={`w-4 h-4 ${iconColor}`} data-id="element-3401" data-mp="33i8ffxywk9sx" /> : <FileText className={`w-4 h-4 ${iconColor}`} data-id="element-3402" data-mp="38g09oyn0s80a" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-3403" data-mp="3debe2tujpqb4">
                            <div className="flex items-center gap-3 mb-1 flex-wrap" data-id="element-3404" data-mp="3dcqozjr02zkn">
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide border ${badgeClass}`} data-id="element-3405" data-mp="3i6czb59oyrda">
                                {finding.severity}
                              </span>
                              <code className="text-[12px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-mono border border-gray-200" data-id="element-3406" data-mp="3w5cfdoj51xxu">
                                {finding.file}{' '}
                                <span className="text-gray-400" data-id="element-3407" data-mp="1r408x4uagxij">
                                  :{finding.line}
                                </span>
                              </code>
                              <span className="text-[11px] text-emerald-600 font-medium ml-auto" data-id="element-3408" data-mp="25upn8g31qsqm">
                                Risparmio: {finding.savings}
                              </span>
                            </div>
                            <p className="text-[13px] text-[#1A202C] mt-1.5" data-id="element-3409" data-mp="2asiqb091z6mo">
                              {finding.desc}
                            </p>
                            <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1" data-id="element-3410" data-mp="2oqz5zgow87gy">
                              <Leaf className="w-3 h-3" data-id="element-3411" data-mp="2jth05z66a8d6" /> {finding.rule}
                            </p>
                          </div>
                        </div>
                      </div>;
            })}
                </div>
              </div>
            </div>}

          {/* Scans Table - always visible */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-3412" data-mp="3142wc5lme71e">
            <div className="px-6 py-5 border-b border-gray-100" data-id="element-3413" data-mp="3635quw3z4uv4">
              <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-3414" data-mp="2rbf5drhbmxal">
                Storico Scansioni
              </h2>
            </div>
            <div className="overflow-x-auto" data-id="element-3415" data-mp="2w8x46p1mjw4v">
              <table className="w-full text-left border-collapse" data-id="element-3416" data-mp="2iagwaus0zzl5">
                <thead data-id="element-3417" data-mp="2nabpx636r39w">
                  <tr className="bg-gray-50/50 border-b border-gray-100" data-id="element-3418" data-mp="2nbmrr0d2m0yd">
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3419" data-mp="28dkn0muaz9fj">
                      Servizio
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3420" data-mp="2dei40w3ktxfg">
                      Repository
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3421" data-mp="bjras2ps5guy">
                      Data Scansione
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-3422" data-mp="23mc1y5x0sdem">
                      Impatto Alto
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-3423" data-mp="1too4h8814rsr">
                      Impatto Medio
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-3424" data-mp="1jymh6ini3dew">
                      Impatto Basso
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-3425" data-mp="1fuwslt6tsvtv">
                      Risparmio Stimato
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3426" data-mp="15y1wfgy0z7ir">
                      Stato
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-3427" data-mp="qus5t2wn8dih">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-3428" data-mp="10uizj2ddqohi">
                  {SCAN_HISTORY.map(scan => <Fragment key={scan.id} data-id="element-3429" data-mp="15rrmdg9h3gp3">
                      <tr className="hover:bg-gray-50/50 transition-colors group" data-id="element-3430" data-mp="85c3qb5b8czy">
                        <td className="px-6 py-4" data-id="element-3431" data-mp="837rn5x9c7ol">
                          <span className="text-[13px] font-medium text-[#2a3a54]" data-id="element-3432" data-mp="d5gle4hyfdrr">
                            {scan.service}
                          </span>
                        </td>
                        <td className="px-6 py-4" data-id="element-3433" data-mp="muq9zadlaeb4">
                          <span className="text-[13px] text-gray-500 font-mono" data-id="element-3434" data-mp="mwb7n8v7add7">
                            {scan.repo}
                          </span>
                        </td>
                        <td className="px-6 py-4" data-id="element-3435" data-mp="3qhqqtfrb77my">
                          <span className="text-[13px] text-gray-500" data-id="element-3436" data-mp="3iic7ax5537dy">
                            {scan.date}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center" data-id="element-3437" data-mp="3dhokyoruveq2">
                          <span className={`text-[13px] font-bold ${scan.high > 0 ? 'text-red-600' : 'text-gray-400'}`} data-id="element-3438" data-mp="3s73zn6e43tpe">
                            {scan.high}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center" data-id="element-3439" data-mp="2zjzxd96elthb">
                          <span className={`text-[13px] font-bold ${scan.medium > 0 ? 'text-amber-600' : 'text-gray-400'}`} data-id="element-3440" data-mp="2ulq2kzolcuw8">
                            {scan.medium}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center" data-id="element-3441" data-mp="34hjc0a70k89s">
                          <span className={`text-[13px] font-bold ${scan.low > 0 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3442" data-mp="oqx2zju46wq4">
                            {scan.low}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-3443" data-mp="rajq6e4te4u8">
                          <span className="text-[13px] font-medium text-emerald-600" data-id="element-3444" data-mp="wbqzwiqipe3j">
                            {scan.co2Savings}
                          </span>
                        </td>
                        <td className="px-6 py-4" data-id="element-3445" data-mp="mcs2jwb7d4vu">
                          {getStatusBadge(scan.status)}
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-3446" data-mp="1a3xcjuugp2y8">
                          <button className="text-[12px] font-medium text-[#00B4D8] hover:text-[#0096b4] transition-colors" data-id="element-3447" data-mp="1f1fnfqs1hs8v">
                            {scan.high > 0 || scan.medium > 0 ? 'Vedi Dettagli' : 'Report'}
                          </button>
                        </td>
                      </tr>
                      {/* Expanded row mock for one item to show findings pattern */}
                      {scan.id === 'sc2' && <tr className="bg-gray-50/30 border-b border-gray-100" data-id="element-3448" data-mp="1xsgl8bhfbhqu">
                          <td colSpan={9} className="px-6 py-4" data-id="element-3449" data-mp="191aqbkambsnm">
                            <div className="bg-white border border-red-100 rounded-lg p-4 shadow-sm" data-id="element-3450" data-mp="193neif6yyz94">
                              <h4 className="text-[12px] font-bold text-red-800 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-3451" data-mp="148rbyl43wthr">
                                <AlertCircle className="w-4 h-4" data-id="element-3452" data-mp="ud6e8z62mfdt" /> Findings
                                Critici (2)
                              </h4>
                              <div className="flex flex-col gap-2" data-id="element-3453" data-mp="1ryc7e3hg5j7x">
                                <div className="flex items-start gap-3 p-3 bg-red-50/50 rounded-md border border-red-100/50" data-id="element-3454" data-mp="1n2n4bq67xk6v">
                                  <div className="mt-0.5" data-id="element-3455" data-mp="1i0eayspfog48">
                                    <FileText className="w-4 h-4 text-red-500" data-id="element-3456" data-mp="1d3xn7zm5c3ro" />
                                  </div>
                                  <div className="flex-1" data-id="element-3457" data-mp="6on74myrc8nb">
                                    <div className="flex justify-between items-start" data-id="element-3458" data-mp="1nhd8ltd2fc7">
                                      <p className="text-[13px] font-medium text-[#1A202C] font-mono" data-id="element-3459" data-mp="2dbm7c36r3ym9">
                                        src/prompts/evaluate.ts{' '}
                                        <span className="text-gray-400 font-normal" data-id="element-3460" data-mp="2i8cnsrmgww87">
                                          line 142
                                        </span>
                                      </p>
                                      <span className="text-[11px] text-emerald-600 font-medium" data-id="element-3461" data-mp="2wzbozhkz4ihr">
                                        Risparmio: ~8 kg CO₂/mese
                                      </span>
                                    </div>
                                    <p className="text-[12px] text-gray-600 mt-1" data-id="element-3462" data-mp="360jd56ts02y4">
                                      I prompt contengono istruzioni ripetute e
                                      contesto statico (~1.800 token) che
                                      potrebbe essere spostato in un template
                                      condiviso.
                                    </p>
                                    <p className="text-[11px] text-red-600 font-medium mt-1 flex items-center gap-1" data-id="element-3463" data-mp="125am4mmadxjb">
                                      <Leaf className="w-3 h-3" data-id="element-3464" data-mp="16zol0z05qmsq" /> Best
                                      Practice: Prompt Engineering
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>}
                    </Fragment>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>}
    </div>;
}